import { TicketStatus, TicketStatusType, TicketStatusFormData } from "@/types/ticketStatus";
import { HttpClient } from "@/api/services/http";
import { TicketPriority, TicketPriorityFormData } from "@/types/ticketPriority";

const API_URL = '/ticket-status';
const PRIORITIES_API_URL = '/ticket-status/priority';
const STATUSES_API_URL = '/ticket-status';
const STATUS_TYPES_API_URL = '/v1/ticket-status-types';

export interface TicketStatusResponse {
    data: TicketStatus[];
    meta: {
        total: number;
        page: number;
        limit: number;
    }
}

export interface TicketStatusTypeResponse {
    data: TicketStatusType[];
    meta: {
        total: number;
        page: number;
        limit: number;
    }
}

export interface TicketPriorityResponse {
    data: TicketPriority[];
    meta: {
        total: number;
        page: number;
        limit: number;
    }
}

export interface TicketPriorityApiResponse {
    status: string;
    message: string;
    data: TicketPriority[];
}

export interface TicketVisibilityRawResponse {
    id: string;
    created_at: string;
    client_id: string;
    workspace_id: string;
    ticket_restriction: boolean;
    team_restriction: boolean;
    teammate_restriction: boolean;
    ticket_prefix?: string | null;
    csat_send_time?: number | null;
    permanent_close_time?: number | null;
}

export interface TicketVisibilityApiResponse {
    status?: string;
    message?: string;
    data?: TicketVisibilityRawResponse;
}

export interface TicketVisibilitySettings {
    id: string;
    createdAt: string;
    clientId: string;
    workspaceId: string;
    ticketRestriction: boolean;
    teamRestriction: boolean;
    teammateRestriction: boolean;
    ticketPrefix?: string | null;
    csatSendTime?: number | null;
    permanentCloseTime?: number | null;
}

export type UpdateTicketVisibilityPayload = Partial<Pick<TicketVisibilitySettings,
    'ticketRestriction' |
    'teamRestriction' |
    'teammateRestriction' |
    'ticketPrefix' |
    'csatSendTime' |
    'permanentCloseTime'
>>;

const extractTicketVisibilityPayload = (raw: TicketVisibilityApiResponse | TicketVisibilityRawResponse): TicketVisibilityRawResponse => {
    if (raw && typeof raw === 'object' && 'data' in raw && raw.data) {
        return raw.data;
    }
    return raw as TicketVisibilityRawResponse;
};

const normalizeTicketVisibility = (raw: TicketVisibilityRawResponse): TicketVisibilitySettings => ({
    id: raw?.id ?? '',
    createdAt: raw?.created_at ?? '',
    clientId: raw?.client_id ?? '',
    workspaceId: raw?.workspace_id ?? '',
    ticketRestriction: Boolean(raw?.ticket_restriction),
    teamRestriction: Boolean(raw?.team_restriction),
    teammateRestriction: Boolean(raw?.teammate_restriction),
    ticketPrefix: raw?.ticket_prefix ?? null,
    csatSendTime: typeof raw?.csat_send_time === 'number' ? raw.csat_send_time : null,
    permanentCloseTime: typeof raw?.permanent_close_time === 'number' ? raw.permanent_close_time : null,
});

export const ticketStatusService = {
    // Legacy endpoints
    listTicketStatusesTypes: async (): Promise<TicketStatusTypeResponse> => {
        const response = await HttpClient.apiClient.get(`${API_URL}`);
        const raw = response.data;

        const rawTypes =
            (Array.isArray(raw?.data) ? raw.data : undefined) ??
            (Array.isArray(raw?.status_types) ? raw.status_types : undefined) ??
            (Array.isArray(raw?.types) ? raw.types : undefined) ??
            [];

        const data: TicketStatusType[] = rawTypes.map((type: any, index: number) => ({
            id: type.id ?? type._id ?? `${index}`,
            name: type.name ?? type.title ?? 'Unnamed',
            description: type.description ?? type.details ?? '',
            color: type.color ?? type.color_code ?? '#64748b',
            order: type.order ?? type.sort_order ?? index,
            isSystemType: Boolean(type.is_system_type ?? type.isSystemType ?? false),
        }));

        const meta = raw?.meta ?? {
            total: data.length,
            page: 1,
            limit: data.length,
        };

        return { data, meta };
    },
    listTicketStatuses: async (): Promise<TicketStatusResponse> => {
        const response = await HttpClient.apiClient.get(`${API_URL}/status`);
        const raw = response.data;

        const rawStatuses =
            (Array.isArray(raw?.data) ? raw.data : undefined) ??
            (Array.isArray(raw?.statuses) ? raw.statuses : undefined) ??
            (Array.isArray(raw?.status_list) ? raw.status_list : undefined) ??
            [];

        const data: TicketStatus[] = rawStatuses.map((status: any, index: number) => ({
            id: status.id ?? status._id ?? `${index}`,
            name: status.name ?? status.title ?? 'Unnamed status',
            statusTypeId:
                status.statusTypeId ??
                status.status_type_id ??
                status.ticket_status_type_id ??
                status.type_id ??
                '',
            color: status.color ?? status.color_code ?? '#2563eb',
            description: status.description ?? status.details ?? '',
            isDefault: Boolean(status.isDefault ?? status.is_default ?? false),
            isSystemStatus: Boolean(status.isSystemStatus ?? status.is_system_status ?? false),
            order: status.order ?? status.sort_order ?? index,
        }));

        const meta = raw?.meta ?? {
            total: data.length,
            page: 1,
            limit: data.length,
        };

        return { data, meta };
    },
    listTicketPriorities: async (): Promise<TicketPriority[]> => {
        const response = await HttpClient.apiClient.get<TicketPriority[]>(`${API_URL}/priority`);
        return response.data;
    },
    listTicketVisibility: async (): Promise<TicketVisibilitySettings> => {
        const response = await HttpClient.apiClient.get<TicketVisibilityApiResponse | TicketVisibilityRawResponse>(`${API_URL}/visibility`);
        const raw = extractTicketVisibilityPayload(response.data);
        return normalizeTicketVisibility(raw);
    },
    updateTicketVisibility: async (payload: UpdateTicketVisibilityPayload): Promise<TicketVisibilitySettings> => {
        const response = await HttpClient.apiClient.put<TicketVisibilityApiResponse | TicketVisibilityRawResponse>(`${API_URL}/visibility`, payload);
        const raw = extractTicketVisibilityPayload(response.data);
        return normalizeTicketVisibility(raw);
    },
    
    // New CRUD API endpoints for Priorities
    createTicketPriority: async (data: TicketPriorityFormData): Promise<TicketPriority[]> => {
        const response = await HttpClient.apiClient.post<TicketPriorityApiResponse>(PRIORITIES_API_URL, data);
        return response.data.data; // Return the full list of priorities
    },
    updateTicketPriority: async (id: string, data: TicketPriorityFormData): Promise<TicketPriority[]> => {
        const response = await HttpClient.apiClient.put<TicketPriorityApiResponse>(`${PRIORITIES_API_URL}/${id}`, data);
        return response.data.data; // Return the full list of priorities
    },
    deleteTicketPriority: async (id: string): Promise<TicketPriority[]> => {
        const response = await HttpClient.apiClient.delete<TicketPriorityApiResponse>(`${PRIORITIES_API_URL}/${id}`);
        return response.data.data; // Return the full list of priorities
    },
    
    // New CRUD API endpoints for Statuses
    createTicketStatus: async (data: TicketStatusFormData & { isDefault?: boolean; isActive?: boolean }): Promise<TicketStatus> => {
        const response = await HttpClient.apiClient.post<TicketStatus>(STATUSES_API_URL, data);
        return response.data;
    },
    updateTicketStatus: async (id: string, data: TicketStatusFormData & { isDefault?: boolean; isActive?: boolean }): Promise<TicketStatus> => {
        const response = await HttpClient.apiClient.put<TicketStatus>(`${STATUSES_API_URL}/${id}`, data);
        return response.data;
    },
    deleteTicketStatus: async (id: string): Promise<void> => {
        await HttpClient.apiClient.delete(`${STATUSES_API_URL}/${id}`);
    },
    
    // Utility API
    getTicketStatusTypes: async (): Promise<TicketStatusType[]> => {
        const response = await HttpClient.apiClient.get<TicketStatusType[]>(STATUS_TYPES_API_URL);
        return response.data;
    }
}
